import { Box, Button, Flex, Icon, Input, NumberInput, Stack, Text, Textarea } from '@chakra-ui/react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { Field } from '@/components/ui/field';
import { blankCurriculumDraft, CurriculumDraft } from '@/pages/planner/draft';
import { PlannerAction } from '@/pages/planner/planner-machine';
import CurWizardHeader from './cur-wizard-header';

interface CurCreateScreenProps {
  // Present when stepping back from curStructure — restores what was typed.
  draft?: CurriculumDraft;
  dispatch: (action: PlannerAction) => void;
}

const curCreateSchema = z.object({
  name: z.string().min(1, 'Curriculum name is required'),
  description: z.string(),
  // Free text like description — optional, so no min() constraint.
  learning_outcomes: z.string(),
  prerequisites: z.string(),
  plan_count: z.coerce.number().int().min(1, 'Must have at least 1 plan').max(16, 'Maximum 16 plans'),
});

type CurCreateValues = z.infer<typeof curCreateSchema>;

// "curCreate" — step 1 of the admin create-curriculum wizard: the
// curriculum-wide details (name, description, learning outcomes,
// prerequisites) plus the plan-slot count (1-16, default 4). Outcomes and
// prerequisites belong here rather than on individual slots: prerequisites are
// what an athlete needs before *starting* the curriculum, outcomes what they
// know after *finishing* it — both describe the whole curriculum. Nothing is
// created here; submitting carries the draft into curStructure (step 2), where
// the shared plan length and sections are set and the curriculum is actually
// created. All four are editable afterwards via curBuild's "Edit details".
export default function CurCreateScreen({ draft, dispatch }: CurCreateScreenProps) {
  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<CurCreateValues>({
    resolver: zodResolver(curCreateSchema),
    defaultValues: {
      name: draft?.name ?? '',
      description: draft?.description ?? '',
      learning_outcomes: draft?.learning_outcomes ?? '',
      prerequisites: draft?.prerequisites ?? '',
      plan_count: draft?.plan_count ?? 4,
    },
  });

  const onSubmit = (values: CurCreateValues) => {
    dispatch({
      type: 'CURRICULUM_INFO_NEXT',
      // Keep the structure already set on step 2 when the admin stepped back;
      // seed the defaults on a first pass through.
      draft: { ...(draft ?? blankCurriculumDraft()), ...values },
    });
  };

  return (
    <Box>
      <CurWizardHeader
        step={1}
        title='Create a curriculum'
        actions={
          <Button
            variant='ghost'
            size='sm'
            onClick={() => dispatch({ type: 'GO_TO', screen: { name: 'curBrowse', intent: 'manage' } })}
          >
            Cancel
          </Button>
        }
      />

      <Text fontSize='sm' color='fg.muted' mt={4} mb={4}>
        Describe the curriculum as a whole — what athletes need going in, and what they&apos;ll be able
        to do coming out. These apply to every plan in it. You&apos;ll set the shared plan structure
        next, then build out each plan.
      </Text>

      <Stack as='form' gap={4} onSubmit={handleSubmit(onSubmit)} maxW='md'>
        <Field label='Curriculum name' invalid={!!errors.name} errorText={errors.name?.message}>
          <Input {...register('name')} placeholder='e.g. U12 Fall Fundamentals' />
        </Field>

        <Field label='Description'>
          <Textarea {...register('description')} rows={3} placeholder='What this curriculum covers (optional)' />
        </Field>

        <Field label='Learning outcomes'>
          <Textarea
            {...register('learning_outcomes')}
            rows={3}
            placeholder='What athletes will be able to do after completing this curriculum (optional)'
          />
        </Field>

        <Field label='Prerequisites'>
          <Textarea
            {...register('prerequisites')}
            rows={2}
            placeholder='What athletes should already know before starting this curriculum (optional)'
          />
        </Field>

        <Field label='Number of plans' invalid={!!errors.plan_count} errorText={errors.plan_count?.message}>
          <Controller
            control={control}
            name='plan_count'
            render={({ field }) => (
              <NumberInput.Root
                min={1}
                max={16}
                value={String(field.value)}
                onValueChange={(details) => field.onChange(details.valueAsNumber)}
                width='140px'
              >
                <NumberInput.Input onBlur={field.onBlur} />
                <NumberInput.Control>
                  <NumberInput.IncrementTrigger>
                    <Icon as={ChevronUpIcon} w={3} h={3} />
                  </NumberInput.IncrementTrigger>
                  <NumberInput.DecrementTrigger>
                    <Icon as={ChevronDownIcon} w={3} h={3} />
                  </NumberInput.DecrementTrigger>
                </NumberInput.Control>
              </NumberInput.Root>
            )}
          />
        </Field>

        <Flex justify='flex-end' gap={3} mt={2}>
          <Button type='submit' colorPalette='brand'>
            Next: plan structure
          </Button>
        </Flex>
      </Stack>
    </Box>
  );
}
